import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation } from 'react-router-dom';

interface AuthLayoutProps {
  children: React.ReactNode;
  title: string;
  subtitle: string;
}

const AuthLayout: React.FC<AuthLayoutProps> = ({ children, title, subtitle }) => {
  const location = useLocation();
  
  return (
    <div className="h-screen bg-surface-dark flex relative overflow-hidden ">
      {/* Floating Bubbles */}
      <div className="absolute top-20 left-40 w-10 h-10 bg-orange-500/30 rounded-full blur-md bubble-1 pointer-events-none" style={{ zIndex: 1 }}></div>
      <div className="absolute top-40 right-60 w-8 h-8 bg-orange-400/25 rounded-full blur-sm bubble-2 pointer-events-none" style={{ zIndex: 1 }}></div>
      <div className="absolute bottom-32 left-80 w-12 h-12 bg-orange-500/20 rounded-full blur-md bubble-3 pointer-events-none" style={{ zIndex: 1 }}></div>
      <div className="absolute top-60 left-1/4 w-6 h-6 bg-orange-600/30 rounded-full blur-sm bubble-1 pointer-events-none" style={{ zIndex: 1 }}></div>
      <div className="absolute bottom-40 right-32 w-10 h-10 bg-orange-400/20 rounded-full blur-md bubble-2 pointer-events-none" style={{ zIndex: 1 }}></div>
      <div className="absolute top-1/3 right-1/4 w-8 h-8 bg-orange-500/25 rounded-full blur-sm bubble-3 pointer-events-none" style={{ zIndex: 1 }}></div>
      <div className="absolute top-3/4 left-60 w-12 h-12 bg-orange-600/20 rounded-full blur-md bubble-1 pointer-events-none" style={{ zIndex: 1 }}></div>
      <div className="absolute bottom-1/4 right-48 w-6 h-6 bg-orange-400/30 rounded-full blur-sm bubble-2 pointer-events-none" style={{ zIndex: 1 }}></div>
      
      {/* Left Side - Promotional Content */}
      <div 
        className="hidden  bg-surface-dark lg:flex lg:w-1/2 xl:w-3/5 p-12 flex-col justify-center items-center relative overflow-y-auto"
        style={{ borderTopLeftRadius: '2rem', borderBottomLeftRadius: '2rem', zIndex: 10 }}
      >
        <div 
          className="absolute inset-0 opacity-7 z-0" 
          style={{
            backgroundImage: 'url(/assets/images/login_pattern.png)',
            backgroundRepeat: 'repeat',
            backgroundSize: '30%',
            backgroundColor: '#212121',
            backgroundPosition: 'center'
          }}
        ></div>
        
        <div className="relative z-10 margin-auto">
          <div className="flex items-center text-center gap-3 mb-10">
            <img 
              src="/assets/images/promo-logo.png"
              alt="PromoFlow Logo" 
              className="h-12 w-auto"
            />
          </div>

          <h1 className="text-3xl xl:text-4xl font-light font-bold text-white mb-6 leading-tight">
            {title}<br />
            <span className="text-4xl xl:text-6xl font-normal text-[#ED4525]">{subtitle}</span>
          </h1>
          <p className="text-gray-400 text-sm font-light b-8 max-w-xl leading-relaxed">
            {title === 'All your campaigns,' 
              ? 'PromoFlowAI is your all-in-one marketing partner, tracking competitors, improving menus, and creating ready-to-post campaigns with posters and captions. Preview, publish, and grow effortlessly.'
              : title === 'Join PromoFlow AI'
              ? 'Create your account and unlock powerful marketing tools, automated campaigns, and AI-driven insights to grow your restaurant business effortlessly.'
              : 'Don\'t worry! It happens to the best of us. Enter your email address and we\'ll send you instructions to reset your password and regain access to your account.'}
          </p>
        </div>
      </div>

      {/* Right Side - Dynamic Content with Animation */}
      <div 
        className="w-full items-center  lg:w-1/2 rounded-t-5xl rounded-l-5xl bg-surface-dark xl:w-2/5 flex flex-col"
        style={{ boxShadow: 'rgba(0, 0, 0, 0.35) 0px 10px 40px 28px inset' }}
      >
        {/* Animated Form Content with 3D Flip */}
        <div 
          className="flex-1 flex items-center justify-center p-6 sm:p-12 overflow-hidden"
          style={{ perspective: '1200px', transformStyle: 'preserve-3d' }}
        >
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={location.pathname}
              initial={{
                rotateY: 90,
                opacity: 0,
                scale: 0.8,
              }}
              animate={{
                rotateY: 0,
                opacity: 1,
                scale: 1,
              }}
              exit={{
                rotateY: -90,
                opacity: 0,
                scale: 0.8,
              }}
              transition={{
                duration: 0.6,
                ease: [0.34, 1.56, 0.64, 1],
                opacity: { duration: 0.4 },
                scale: { duration: 0.5 },
              }}
              style={{
                transformStyle: 'preserve-3d',
                backfaceVisibility: 'hidden',
              }}
              className="w-full"
            >
              {children}
            </motion.div>
          </AnimatePresence>
        </div>
        
        {/* Footer at bottom - Always visible */}
        <div className="px-6 pb-6 text-center border-t border-gray-800/50">
          <p className="text-gray-400 text-xs mb-2 mt-4">© 2026 PromoFlow AI</p>
          <div className="flex justify-center gap-4 text-xs">
            <a href="#" className="text-gray-400 hover:text-orange-500 transition-colors">
              Privacy Policy
            </a>
            <span className="text-gray-700">|</span>
            <a href="#" className="text-gray-400 hover:text-orange-500 transition-colors">
              Terms of Service
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthLayout;
